import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Building2, Loader2, AlertCircle, CheckCircle, ShieldCheck, XCircle, UserCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PageHeader } from "@/components/PageHeader";
import { getApplication, submitBankDetails, runCoPCheck, getApplicationBeneficiary } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const nameKey = (v) => (v || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

export default function AddBankDetails() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  const admin = isAdmin();
  
  const [application, setApplication] = useState(null);
  const [previous, setPrevious] = useState(null);   // the beneficiary that was reset — pre-fills so only the payee name needs fixing
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state - pre-fill from URL params if available
  const [accountNumber, setAccountNumber] = useState(searchParams.get("accountNumber") || "");
  const [sortCode, setSortCode] = useState(searchParams.get("sortCode") || "");
  const [bankName, setBankName] = useState(searchParams.get("bankName") || "");
  const [accountName, setAccountName] = useState(searchParams.get("accountName") || "");
  const [accountNameReason, setAccountNameReason] = useState("");
  const [copVerified, setCopVerified] = useState(false);
  const [copStatus, setCopStatus] = useState(null); // null, "verifying", "verified", "divergence", "partial", "failed", "no_match"
  const [copResult, setCopResult] = useState(null); // Full CoP result for displaying details
  
  // Validation
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadApplication();
  }, [appId]);

  const nameOverridden = !!accountName.trim() && nameKey(accountName) !== nameKey(application?.company_name);
  const bankHeldName = previous?.bank_registered_name || application?.cop_check_returned_name || "";

  const loadApplication = async () => {
    try {
      setLoading(true);
      const data = await getApplication(appId);
      setApplication(data);
      try {
        const b = await getApplicationBeneficiary(appId);
        const prev = b?.previous || null;
        setPrevious(prev);
        if (prev && !searchParams.get("accountNumber")) {
          if (prev.full_account_number) setAccountNumber(prev.full_account_number);
          if (prev.full_sort_code) setSortCode(data?.country === "UK" ? formatSortCode(prev.full_sort_code) : prev.full_sort_code);
          if (prev.bank_name) setBankName(prev.bank_name);
          if (prev.account_name) setAccountName(prev.account_name);
        }
      } catch { /* no previous details — plain form */ }
    } catch (error) {
      console.error("Failed to load application:", error);
      toast.error("Failed to load application");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!accountNumber.trim()) {
      newErrors.accountNumber = "Account number is required";
    } else if (application?.country === "UK" && !/^\d{8}$/.test(accountNumber.replace(/\s/g, ""))) {
      newErrors.accountNumber = "UK account numbers must be 8 digits";
    }
    
    if (!sortCode.trim()) {
      newErrors.sortCode = "Sort code is required";
    } else if (application?.country === "UK") {
      const cleanSortCode = sortCode.replace(/[-\s]/g, "");
      if (!/^\d{6}$/.test(cleanSortCode)) {
        newErrors.sortCode = "UK sort codes must be 6 digits";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerifyCoP = async () => {
    if (!validateForm()) return;
    
    try {
      setCopStatus("verifying");
      
      // First submit the bank details
      await submitBankDetails(appId, {
        account_number: accountNumber.replace(/\s/g, ""),
        sort_code: sortCode.replace(/[-\s]/g, ""),
        bank_name: bankName || null,
        account_name: admin && accountName.trim() ? accountName.trim() : null,
        account_name_reason: admin && nameOverridden ? (accountNameReason.trim() || null) : null,
        submitted_by: user?.email || "sales@simplyfunded.com",
      });
      
      // Then run CoP check
      const result = await runCoPCheck(appId, user?.email || "system");
      setCopResult(result); // Store full result for display
      
      if (result.match_status === "full_match") {
        setCopStatus("verified");
        setCopVerified(true);
        toast.success("Bank details verified via Confirmation of Payee");
      } else if (result.match_status === "name_divergence") {
        setCopStatus("divergence");
        setCopVerified(false);
        toast.warning("Matched at the bank — sent to Credit to approve the payee name");
      } else if (result.match_status === "partial_match") {
        setCopStatus("partial");
        setCopVerified(false);
        toast.warning("Partial match - will require Credit approval");
      } else if (result.match_status === "no_match") {
        setCopStatus("no_match");
        setCopVerified(false);
        toast.error("No match - bank details do not match");
      } else {
        setCopStatus("failed");
        setCopVerified(false);
        toast.error("CoP check failed - will require Credit review");
      }
    } catch (error) {
      console.error("Failed to verify:", error);
      setCopStatus("failed");
      toast.error(error.response?.data?.detail || "Failed to verify bank details");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (copStatus === "verified") {
      // Already verified, just navigate back
      toast.success("Bank details submitted successfully");
      navigate(`/workflow/${appId}`);
      return;
    }
    
    if (["partial", "failed", "no_match", "divergence"].includes(copStatus)) {
      // Submit for Credit review
      try {
        setSubmitting(true);
        toast.success("Bank details submitted for Credit review");
        navigate(`/workflow/${appId}`);
      } catch (error) {
        toast.error("Failed to submit for review");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    
    // Should not reach here, but just in case
    toast.error("Please verify the bank details first");
  };

  const formatSortCode = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Application Not Found</h2>
        <Button onClick={() => navigate("/")} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Deals
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="add-bank-details-page">
      <PageHeader
        title="Add Bank Details"
        description={`For ${application.company_name} (${application.application_id})`}
        breadcrumb={
          <Link 
            to={`/workflow/${appId}`} 
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Workflow
          </Link>
        }
      />

      <div className="max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle>Bank Account Information</CardTitle>
                <CardDescription>
                  Enter the bank details for {application.company_name}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Account Number */}
              <div className="space-y-2">
                <Label htmlFor="accountNumber">
                  Account Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="accountNumber"
                  type="text"
                  placeholder={application.country === "UK" ? "12345678" : "123456789"}
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  className={errors.accountNumber ? "border-destructive" : ""}
                  data-testid="account-number-input"
                />
                {errors.accountNumber && (
                  <p className="text-sm text-destructive">{errors.accountNumber}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {application.country === "UK" ? "8 digit UK account number" : "Australian BSB account number"}
                </p>
              </div>

              {/* Sort Code / BSB */}
              <div className="space-y-2">
                <Label htmlFor="sortCode">
                  {application.country === "UK" ? "Sort Code" : "BSB"} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="sortCode"
                  type="text"
                  placeholder={application.country === "UK" ? "00-00-00" : "000-000"}
                  value={sortCode}
                  onChange={(e) => setSortCode(application.country === "UK" ? formatSortCode(e.target.value) : e.target.value)}
                  className={errors.sortCode ? "border-destructive" : ""}
                  data-testid="sort-code-input"
                />
                {errors.sortCode && (
                  <p className="text-sm text-destructive">{errors.sortCode}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {application.country === "UK" ? "6 digit sort code (e.g., 12-34-56)" : "6 digit BSB number"}
                </p>
              </div>

              {/* Bank Name */}
              <div className="space-y-2">
                <Label htmlFor="bankName">Bank Name (Optional)</Label>
                <Input
                  id="bankName"
                  type="text"
                  placeholder="e.g., Barclays, HSBC, Lloyds"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  data-testid="bank-name-input"
                />
              </div>

              {/* Payee (account) name — Admins may hard-enter it when the bank holds the account under a different form of the name */}
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="payee-name-block">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-slate-600" />
                  <Label htmlFor="accountName">Payee name at the bank {admin ? <span className="text-xs font-normal text-muted-foreground">(Admin — leave blank to use the registered name)</span> : null}</Label>
                </div>
                {admin ? (
                  <>
                    <Input
                      id="accountName"
                      type="text"
                      placeholder={application.company_name}
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      data-testid="account-name-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Registered name: <span className="font-mono">{application.company_name}</span>. CoP runs against the name you enter here and the payout goes out in it —
                      a different name always goes to Credit for approval, even on a full match.
                    </p>
                    {bankHeldName && nameKey(bankHeldName) !== nameKey(accountName) && (
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAccountName(bankHeldName)} data-testid="use-bank-held-name-btn">
                        Use the name the bank holds: <span className="font-mono ml-1">{bankHeldName}</span>
                      </Button>
                    )}
                    {nameOverridden && (
                      <div className="space-y-1 pt-1">
                        <Label htmlFor="accountNameReason" className="text-xs">Why this name? (shown to Credit)</Label>
                        <Input
                          id="accountNameReason"
                          type="text"
                          placeholder="e.g. Bank holds the account without the middle names — CoP passed in Capay with this form"
                          value={accountNameReason}
                          onChange={(e) => setAccountNameReason(e.target.value)}
                          data-testid="account-name-reason-input"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="payee-name-readonly">
                    CoP will check <span className="font-mono">{previous?.account_name || application.company_name}</span>. Only an Admin can set a different payee name.
                  </p>
                )}
              </div>

              {/* CoP Verification Status */}
              {copStatus && (
                <div className={`p-4 rounded-lg border-2 ${
                  copStatus === "verified" ? "bg-emerald-50 border-emerald-300" :
                  copStatus === "partial" || copStatus === "divergence" ? "bg-amber-50 border-amber-300" :
                  copStatus === "failed" ? "bg-red-50 border-red-300" :
                  "bg-blue-50 border-blue-300"
                }`}>
                  {copStatus === "verifying" && (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      <p className="text-blue-800">Verifying bank details via Confirmation of Payee...</p>
                    </div>
                  )}
                  {copStatus === "verified" && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <p className="font-medium text-emerald-800">Bank details verified successfully!</p>
                    </div>
                  )}
                  {copStatus === "divergence" && (
                    <div className="space-y-2" data-testid="cop-divergence-result">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <p className="font-medium text-amber-800">✓ Matched at the bank — Credit must approve the payee name</p>
                      </div>
                      <p className="text-sm text-amber-800">
                        <span className="font-mono font-semibold">{copResult?.submitted_name}</span> is the account holder, but it differs from the registered name
                        <span className="font-mono"> {application?.company_name}</span>. The payout will go out in the approved name.
                      </p>
                      {copResult?.reasons?.length > 0 && (
                        <ul className="text-xs text-amber-800 space-y-0.5">{copResult.reasons.map((r, i) => <li key={i}>• {r}</li>)}</ul>
                      )}
                    </div>
                  )}
                  {copStatus === "partial" && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <p className="font-medium text-amber-800">⚠ Partial Match - Name Variation Detected</p>
                      </div>
                      
                      {/* Detailed CoP Information */}
                      {copResult && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-amber-600">Name Submitted:</span>
                              <p className="font-medium text-amber-900">{copResult.submitted_name || application?.company_name || "N/A"}</p>
                            </div>
                            {copResult.returned_name && (
                              <div>
                                <span className="text-amber-600">Name on Bank Account:</span>
                                <p className="font-medium text-amber-900">{copResult.returned_name}</p>
                              </div>
                            )}
                          </div>
                          
                          {copResult.name_match_result && (
                            <div className="pt-2 border-t border-amber-200">
                              <span className="text-amber-600">Match Result:</span>
                              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                                copResult.name_match_result === "Full" ? "bg-emerald-100 text-emerald-800" :
                                copResult.name_match_result === "Close" ? "bg-amber-100 text-amber-800" :
                                "bg-red-100 text-red-800"
                              }`}>
                                {copResult.name_match_result}
                              </span>
                            </div>
                          )}
                          
                          {copResult.reasons?.length > 0 && (
                            <div className="pt-2 border-t border-amber-200">
                              <span className="text-amber-600">Verification Details:</span>
                              <ul className="mt-1 space-y-1">
                                {copResult.reasons.map((reason, idx) => (
                                  <li key={idx} className="text-amber-800">• {reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {copResult.result_text && (
                            <div className="pt-2 border-t border-amber-200">
                              <span className="text-amber-600">Bank Response:</span>
                              <p className="text-amber-800 mt-1">{copResult.result_text}</p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <p className="text-sm text-amber-700">
                        The name on the bank account doesn&apos;t exactly match. You can submit for Credit review or update the details and try again.
                      </p>
                    </div>
                  )}
                  {copStatus === "no_match" && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-600" />
                        <p className="font-medium text-red-800">✗ No Match - Bank Details Rejected</p>
                      </div>
                      
                      {/* Detailed CoP Information */}
                      {copResult && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-red-600">Name Submitted:</span>
                              <p className="font-medium text-red-900">{copResult.submitted_name || application?.company_name || "N/A"}</p>
                            </div>
                            {copResult.returned_name && (
                              <div>
                                <span className="text-red-600">Name on Bank Account:</span>
                                <p className="font-medium text-red-900">{copResult.returned_name}</p>
                              </div>
                            )}
                          </div>
                          
                          {copResult.name_match_result && (
                            <div className="pt-2 border-t border-red-200">
                              <span className="text-red-600">Match Result:</span>
                              <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                {copResult.name_match_result}
                              </span>
                            </div>
                          )}
                          
                          {copResult.reasons?.length > 0 && (
                            <div className="pt-2 border-t border-red-200">
                              <span className="text-red-600">Verification Details:</span>
                              <ul className="mt-1 space-y-1">
                                {copResult.reasons.map((reason, idx) => (
                                  <li key={idx} className="text-red-800">• {reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {copResult.result_text && (
                            <div className="pt-2 border-t border-red-200">
                              <span className="text-red-600">Bank Response:</span>
                              <p className="text-red-800 mt-1">{copResult.result_text}</p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <p className="text-sm text-red-700">
                        The bank account name does not match. Please verify the details are correct and try again, or submit for Credit review.
                      </p>
                    </div>
                  )}
                  {copStatus === "failed" && (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <p className="font-medium text-red-800">Verification Failed</p>
                      </div>
                      <p className="text-sm text-red-700 mb-2">Could not verify bank details. You can submit for Credit review or correct the details and try again.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/workflow/${appId}`)}
                >
                  Cancel
                </Button>
                
                {/* Initial state - show Verify button */}
                {!copStatus && (
                  <Button
                    type="button"
                    onClick={handleVerifyCoP}
                    disabled={!accountNumber || !sortCode}
                    data-testid="verify-cop-btn"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Verify Bank Details
                  </Button>
                )}
                
                {/* Verifying state */}
                {copStatus === "verifying" && (
                  <Button type="button" disabled>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </Button>
                )}
                
                {/* Verified - show Continue button */}
                {copStatus === "verified" && (
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="continue-btn"
                  >
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Continue to Workflow
                  </Button>
                )}
                
                {/* Partial / Failed / No match / Divergence - show both options */}
                {["partial", "failed", "no_match", "divergence"].includes(copStatus) && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setCopStatus(null);
                        setCopVerified(false);
                      }}
                      data-testid="edit-details-btn"
                    >
                      Edit & Re-verify
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="submit-for-review-btn"
                    >
                      {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {copStatus === "divergence" ? "Done — waiting for Credit" : "Submit for Credit Review"}
                    </Button>
                  </>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Alert className="mt-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Bank details will be reviewed by the credit team before verification.
            You&apos;ll be notified once approved or if any issues are found.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
